import { useCallback, useEffect, useState } from 'react'
import {
  activateEntitlement,
  listEntitlements,
  listXeroInvoiceStates,
  passLicenseGate,
  passPaymentGate,
} from '../../services/licensing'
import type { Entitlement, ImportResponse, XeroInvoiceState } from '../../types'

export type ImportMode = 'none' | 'json' | 'manual'

export interface LicenseActivationState {
  entitlements: Entitlement[]
  xeroStates: Map<string, XeroInvoiceState>
  loading: boolean
  error: string | null
  setError: React.Dispatch<React.SetStateAction<string | null>>
  selectedEntitlementId: string | null
  setSelectedEntitlementId: React.Dispatch<React.SetStateAction<string | null>>
  selectedEntitlement: Entitlement | undefined
  selectedXeroState: XeroInvoiceState | null
  importMode: ImportMode
  setImportMode: React.Dispatch<React.SetStateAction<ImportMode>>
  isProcessing: boolean
  showImportSection: boolean
  setShowImportSection: React.Dispatch<React.SetStateAction<boolean>>
  loadData: () => Promise<void>
  handleImportSuccess: (result: ImportResponse) => Promise<void>
  handleImportError: (err: Error) => void
  handlePassPaymentGate: () => Promise<void>
  handlePassLicenseGate: () => Promise<void>
  handleActivate: () => Promise<void>
}

function buildXeroStateMap(states: XeroInvoiceState[]): Map<string, XeroInvoiceState> {
  const map = new Map<string, XeroInvoiceState>()
  for (const state of states) {
    if (state.entitlementId) map.set(state.entitlementId, state)
  }
  return map
}

interface LoadDataDeps {
  selectedEntitlementId: string | null
  setEntitlements: React.Dispatch<React.SetStateAction<Entitlement[]>>
  setXeroStates: React.Dispatch<React.SetStateAction<Map<string, XeroInvoiceState>>>
  setSelectedEntitlementId: React.Dispatch<React.SetStateAction<string | null>>
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  setError: React.Dispatch<React.SetStateAction<string | null>>
}

function useLoadData(deps: LoadDataDeps): () => Promise<void> {
  const {
    selectedEntitlementId,
    setEntitlements,
    setXeroStates,
    setSelectedEntitlementId,
    setLoading,
    setError,
  } = deps
  return useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [entitlementsRes, xeroRes] = await Promise.all([
        listEntitlements({ limit: 100 }),
        listXeroInvoiceStates({ limit: 100 }),
      ])
      setEntitlements(entitlementsRes.data)
      setXeroStates(buildXeroStateMap(xeroRes.data))
      if (!selectedEntitlementId && entitlementsRes.data.length > 0) {
        setSelectedEntitlementId(entitlementsRes.data[0]?.id ?? null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [
    selectedEntitlementId,
    setEntitlements,
    setXeroStates,
    setSelectedEntitlementId,
    setLoading,
    setError,
  ])
}

interface GateActionDeps {
  selectedEntitlementId: string | null
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>
  setError: React.Dispatch<React.SetStateAction<string | null>>
  loadData: () => Promise<void>
  fn: (id: string) => Promise<unknown>
  fallbackMessage: string
}

function useGateAction(deps: GateActionDeps): () => Promise<void> {
  return async (): Promise<void> => {
    if (!deps.selectedEntitlementId) return
    deps.setIsProcessing(true)
    try {
      await deps.fn(deps.selectedEntitlementId)
      await deps.loadData()
    } catch (err) {
      deps.setError(err instanceof Error ? err.message : deps.fallbackMessage)
    } finally {
      deps.setIsProcessing(false)
    }
  }
}

export function useLicenseActivation(): LicenseActivationState {
  const [entitlements, setEntitlements] = useState<Entitlement[]>([])
  const [xeroStates, setXeroStates] = useState<Map<string, XeroInvoiceState>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedEntitlementId, setSelectedEntitlementId] = useState<string | null>(null)
  const [importMode, setImportMode] = useState<ImportMode>('none')
  const [isProcessing, setIsProcessing] = useState(false)
  const [showImportSection, setShowImportSection] = useState(true)

  const loadData = useLoadData({
    selectedEntitlementId,
    setEntitlements,
    setXeroStates,
    setSelectedEntitlementId,
    setLoading,
    setError,
  })

  useEffect(() => {
    void loadData()
  }, [loadData])

  const selectedEntitlement = entitlements.find((e) => e.id === selectedEntitlementId)
  const selectedXeroState = selectedEntitlementId
    ? (xeroStates.get(selectedEntitlementId) ?? null)
    : null

  const handleImportSuccess = async (_result: ImportResponse): Promise<void> => {
    setImportMode('none')
    await loadData()
  }

  const handleImportError = (err: Error): void => {
    setError(err.message)
  }

  const baseGateDeps = { selectedEntitlementId, setIsProcessing, setError, loadData }
  const handlePassPaymentGate = useGateAction({
    ...baseGateDeps,
    fn: passPaymentGate,
    fallbackMessage: 'Failed to pass payment gate',
  })
  const handlePassLicenseGate = useGateAction({
    ...baseGateDeps,
    fn: passLicenseGate,
    fallbackMessage: 'Failed to pass license gate',
  })
  const handleActivate = useGateAction({
    ...baseGateDeps,
    fn: activateEntitlement,
    fallbackMessage: 'Failed to activate entitlement',
  })

  return {
    entitlements,
    xeroStates,
    loading,
    error,
    setError,
    selectedEntitlementId,
    setSelectedEntitlementId,
    selectedEntitlement,
    selectedXeroState,
    importMode,
    setImportMode,
    isProcessing,
    showImportSection,
    setShowImportSection,
    loadData,
    handleImportSuccess,
    handleImportError,
    handlePassPaymentGate,
    handlePassLicenseGate,
    handleActivate,
  }
}
